import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isExcludableGeoType } from '../../../lib/metaExclusionSupport';
import { useDebouncedValue } from '../useDebouncedValue';
import { GEO_TYPE_LABEL, groupGeoResults, searchAllGeoTypes, type UnifiedGeoResult } from '../geoSearch';

// Audience-builder — the ONE location search box. Fans out across all geo types and
// shows the merged results GROUPED by type, each with its full path breadcrumb (the
// breadcrumb is what lets the AM pick the RIGHT "Holland Park" out of many). Selecting a
// result hands it to the caller, which applies the hierarchy logic.

export function UnifiedGeoSearch({
  accountId,
  biasCountry,
  onSelect,
  mode = 'include',
  onModeChange,
}: {
  accountId: string;
  // The account's currency-derived country — biases ranking only (the search is global).
  biasCountry: string | null;
  onSelect: (result: UnifiedGeoResult) => void;
  // Include (default) routes a pick into the targeted audience; Exclude carves the
  // picked area OUT (into the excluded bucket). Omit onModeChange to hide the toggle.
  mode?: 'include' | 'exclude';
  onModeChange?: (mode: 'include' | 'exclude') => void;
}) {
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q.trim(), 300);

  const results = useQuery({
    queryKey: ['unifiedGeoSearch', accountId, biasCountry, debounced],
    queryFn: () => searchAllGeoTypes(accountId, debounced, biasCountry),
    enabled: accountId.trim().length > 0 && debounced.length > 0,
  });

  const groups = groupGeoResults(results.data ?? []);
  const open = debounced.length > 0;
  const excluding = mode === 'exclude';

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-slate-700">Locations</label>
        {onModeChange && (
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs" role="group" aria-label="Include or exclude locations">
            <button
              type="button"
              aria-pressed={!excluding}
              onClick={() => onModeChange('include')}
              className={`px-2 py-0.5 ${!excluding ? 'bg-kyck-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Include
            </button>
            <button
              type="button"
              aria-pressed={excluding}
              onClick={() => onModeChange('exclude')}
              className={`px-2 py-0.5 ${excluding ? 'bg-red-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Exclude
            </button>
          </div>
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={
            accountId
              ? excluding
                ? 'Search an area to EXCLUDE — state, city, suburb, postcode…'
                : 'Search countries, states, cities, suburbs, postcodes…'
              : 'Pick an account first'
          }
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            excluding
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-kyck-500 focus:ring-kyck-500'
          }`}
        />
        {open && groups.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-80 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-md">
            {groups.map((g) => (
              <div key={g.geoType}>
                <div className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {g.label}
                </div>
                {g.items.slice(0, 8).map((r) => {
                  // In Exclude mode, only region/city/zip can be excluded — Meta drops
                  // excluded suburbs/neighborhoods. Show them DISABLED ("can't exclude")
                  // rather than letting the AM pick something that would silently vanish.
                  const notExcludable = excluding && !isExcludableGeoType(r.geoType);
                  return (
                    <button
                      key={`${r.geoType}:${r.id}`}
                      type="button"
                      disabled={notExcludable}
                      onClick={() => {
                        if (notExcludable) return;
                        onSelect(r);
                        setQ('');
                      }}
                      className={`flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left ${
                        notExcludable ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className="flex flex-col leading-tight">
                        <span className="text-sm text-slate-700">{r.name}</span>
                        {r.path && r.path.length > 0 && (
                          <span className="text-xs text-slate-400">{r.path.join(', ')}</span>
                        )}
                      </span>
                      <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        {notExcludable ? "can't exclude" : GEO_TYPE_LABEL[r.geoType]}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {open && !results.isFetching && groups.length === 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-400 shadow-md">
            No matching locations.
          </div>
        )}
      </div>
    </div>
  );
}
